import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpCircle, ArrowDownCircle, RotateCcw, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRegisterRawMaterialMovement } from "@/hooks/useRawMaterialMovements";

interface RawMaterialMovementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  materialId: string;
  materialName: string;
  currentStock: number;
  currentSquareMeters?: number;
  unit: string;
  movementType: "in" | "out" | "adjustment";
  onSuccess?: () => void;
}

export function RawMaterialMovementDialog({
  open,
  onOpenChange,
  materialId,
  materialName,
  currentStock,
  currentSquareMeters,
  unit,
  movementType,
  onSuccess,
}: RawMaterialMovementDialogProps) {
  const registerMovement = useRegisterRawMaterialMovement();
  const [formData, setFormData] = useState({
    quantity: "",
    squareMeters: "",
    reason: "",
    notes: "",
    batchNumber: "",
    rollNumber: "",
  });

  const inReasons = [
    { value: "purchase", label: "Compra" },
    { value: "return", label: "Devolución" },
    { value: "transfer_in", label: "Transferencia entrada" },
    { value: "production_return", label: "Retorno de producción" },
    { value: "initial", label: "Stock inicial" },
    { value: "other", label: "Otro" },
  ];

  const outReasons = [
    { value: "production", label: "Producción" },
    { value: "consumption", label: "Consumo" },
    { value: "damage", label: "Material dañado" },
    { value: "expired", label: "Vencido" },
    { value: "transfer_out", label: "Transferencia salida" },
    { value: "sample", label: "Muestra" },
    { value: "other", label: "Otro" },
  ];

  const adjustmentReasons = [
    { value: "correction", label: "Corrección de inventario" },
    { value: "recount", label: "Reconteo físico" },
    { value: "system_error", label: "Error de sistema" },
    { value: "other", label: "Otro" },
  ];

  const reasons =
    movementType === "in"
      ? inReasons
      : movementType === "out"
      ? outReasons
      : adjustmentReasons;

  const getTitle = () => {
    switch (movementType) {
      case "in":
        return "Registrar Entrada";
      case "out":
        return "Registrar Salida";
      case "adjustment":
        return "Ajuste de Inventario";
    }
  };

  const getIcon = () => {
    switch (movementType) {
      case "in":
        return <ArrowUpCircle className="w-5 h-5 text-green-500" />;
      case "out":
        return <ArrowDownCircle className="w-5 h-5 text-red-500" />;
      case "adjustment":
        return <RotateCcw className="w-5 h-5 text-blue-500" />;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const quantity = parseFloat(formData.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      return;
    }

    if (!formData.reason) {
      return;
    }

    if (!formData.batchNumber.trim()) {
      return;
    }

    if (movementType === "out" && quantity > currentStock) {
      return;
    }

    const sqmChange = formData.squareMeters ? parseFloat(formData.squareMeters) : undefined;

    await registerMovement.mutateAsync({
      materialId,
      movementType,
      quantity,
      squareMetersChange: sqmChange,
      reason: formData.reason,
      notes: formData.notes,
      currentStock,
      currentSquareMeters,
      batchNumber: formData.batchNumber,
      rollNumber: formData.rollNumber,
    });

    onOpenChange(false);
    resetForm();
    onSuccess?.();
  };

  const resetForm = () => {
    setFormData({
      quantity: "",
      squareMeters: "",
      reason: "",
      notes: "",
      batchNumber: "",
      rollNumber: "",
    });
  };

  const getNewStock = () => {
    const qty = parseFloat(formData.quantity) || 0;
    if (movementType === "in") return currentStock + qty;
    if (movementType === "out") return currentStock - qty;
    return qty; // adjustment
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getIcon()}
            {getTitle()}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Material: {materialName}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Current Stock Info */}
          <div className="p-3 rounded-lg bg-muted/50 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Stock actual</span>
              <span className="font-bold text-lg">
                {currentStock} {unit}
              </span>
            </div>
            {currentSquareMeters !== undefined && currentSquareMeters > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">m² actuales</span>
                <span className="font-medium">{currentSquareMeters} m²</span>
              </div>
            )}
          </div>

          {/* Quantity */}
          <div className="space-y-2">
            <Label>
              {movementType === "adjustment" ? "Nuevo Stock *" : "Cantidad *"}
            </Label>
            <div className="relative">
              <Input
                type="number"
                min="0"
                step="0.001"
                value={formData.quantity}
                onChange={(e) =>
                  setFormData({ ...formData, quantity: e.target.value })
                }
                placeholder={movementType === "adjustment" ? "Cantidad total" : "0"}
                className={cn(
                  "pr-16",
                  movementType === "in" && "border-green-500 focus:ring-green-500",
                  movementType === "out" && "border-red-500 focus:ring-red-500"
                )}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                {unit}
              </span>
            </div>
            {movementType === "out" && (
              <p className="text-xs text-muted-foreground">
                Máximo disponible: {currentStock} {unit}
              </p>
            )}
          </div>

          {/* Square Meters (optional) */}
          {(currentSquareMeters !== undefined || movementType === "in") && (
            <div className="space-y-2">
              <Label>
                {movementType === "adjustment" ? "Nuevos m²" : "Metros Cuadrados"}
              </Label>
              <div className="relative">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.squareMeters}
                  onChange={(e) =>
                    setFormData({ ...formData, squareMeters: e.target.value })
                  }
                  placeholder="0.00"
                  className="pr-12"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  m²
                </span>
              </div>
            </div>
          )}

          {/* Batch Number and Roll Number */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Lote *</Label>
              <Input
                value={formData.batchNumber}
                onChange={(e) =>
                  setFormData({ ...formData, batchNumber: e.target.value })
                }
                placeholder="Ej: LOT-2026-001"
              />
            </div>
            <div className="space-y-2">
              <Label>Rollo</Label>
              <Input
                value={formData.rollNumber}
                onChange={(e) =>
                  setFormData({ ...formData, rollNumber: e.target.value })
                }
                placeholder="Ej: R-001"
              />
            </div>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label>Motivo *</Label>
            <Select
              value={formData.reason}
              onValueChange={(value) =>
                setFormData({ ...formData, reason: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar motivo" />
              </SelectTrigger>
              <SelectContent>
                {reasons.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Observaciones adicionales..."
              rows={2}
            />
          </div>

          {/* Preview */}
          {formData.quantity && !isNaN(parseFloat(formData.quantity)) && (
            <div className="p-3 rounded-lg border bg-muted/30">
              <p className="text-sm text-muted-foreground mb-1">
                Stock después del movimiento:
              </p>
              <p className="font-bold text-lg">
                {getNewStock().toFixed(3)} {unit}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={registerMovement.isPending || !formData.quantity || !formData.reason || !formData.batchNumber.trim()}
              className={cn(
                movementType === "in" && "bg-green-600 hover:bg-green-700",
                movementType === "out" && "bg-red-600 hover:bg-red-700"
              )}
            >
              {registerMovement.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Procesando...
                </>
              ) : (
                "Confirmar"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
